import React, { useState, useEffect } from "react";
import ScheduleModal from "./ScheduleModal";
import { shortPref, prefClass } from "../shiftPrefs";
import "./EmployeePanel.css";

function initialsFor(first, last) { return `${(first || "?")[0]}${(last || "?")[0]}`.toUpperCase(); }
// Caregivers are external — a plain tel: link is the reliable click-to-call
// (hands the E.164 number to the machine's default calling app), rather than a
// Teams user-call by email or a license-gated Teams PSTN deep link.
function e164(phone) {
  if (!phone) return null;
  const n = String(phone).trim();
  if (n.startsWith("+")) return "+" + n.slice(1).replace(/\D/g, "");
  const d = n.replace(/\D/g, "");
  if (d.length === 10) return "+1" + d;
  if (d.length === 11 && d[0] === "1") return "+" + d;
  return d ? "+" + d : null;
}
function callUrl(phone) {
  const n = e164(phone);
  return n ? `tel:${n}` : null;
}
function mapsUrl(origin, dest) {
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}&travelmode=driving`;
}
// Public-transit estimate ~2.5x drive time (same approximation as the dispatch view).
function transitFor(min) { return (min != null) ? Math.round((min * 2.5) / 5) * 5 : null; }

function typeLabel(clientType) {
  const t = (clientType || "").toLowerCase();
  if (t.startsWith("4b13e")) return "GEM";
  if (t.startsWith("884e8f")) return "Facility";
  if (t.startsWith("505c")) return "Home Care";
  return "Other";
}
function fmtDate(ts) {
  if (!ts) return "";
  const d = new Date(ts), today = new Date(), tom = new Date(today); tom.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === tom.toDateString()) return "Tomorrow";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function fmtTime(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
function shiftHours(start, end) {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return ms > 0 ? ms / 3600000 : null;
}

const AVATAR_COLORS = [
  ["#1a3a2a", "#3ecf8e"], ["#1a2a3a", "#4d9ef7"], ["#2a1a3a", "#a78bfa"],
  ["#3a2a1a", "#f5a623"], ["#2a1a1a", "#e05c5c"], ["#1a3a3a", "#26C6DA"],
  ["#3a1a2a", "#F48FB1"], ["#2a3a1a", "#AED581"],
];
function avatarColor(id) { return AVATAR_COLORS[(id?.charCodeAt(0) || 0) % AVATAR_COLORS.length]; }

function DistLine({ dist }) {
  if (!dist || dist.miles == null) return <div className="emp-card-dist muted">Distance unavailable</div>;
  const transit = transitFor(dist.minutes);
  return (
    <div className="emp-card-dist">
      <span className="emp-mi">{dist.miles} mi</span>
      <span className="emp-sep">·</span>
      <span>~{dist.minutes} min drive</span>
      {transit != null && (
        <>
          <span className="emp-sep">·</span>
          <span className="emp-transit">🚌 ~{transit} min transit</span>
        </>
      )}
    </div>
  );
}

export default function EmployeePanel({
  employee, clients = [], shifts = [], distances = {}, loadingDist, onComputeDistances,
  schedule, weekStart, weekEnd, isDNR, isPreferred,
}) {
  const [view, setView] = useState("shifts");
  const [showSchedule, setShowSchedule] = useState(false);
  const [editedAddress, setEditedAddress] = useState(employee?.address || "");
  const [editingAddress, setEditingAddress] = useState(false);

  // Reset the editable address whenever a different employee is selected.
  useEffect(() => {
    setEditedAddress(employee?.address || "");
    setEditingAddress(false);
  }, [employee?.id, employee?.address]);

  if (!employee) {
    return (
      <div className="employee-empty">
        <div className="empty-icon">◈</div>
        <p>Select an employee from the list<br />to suggest nearby shifts and client locations</p>
      </div>
    );
  }

  const [bg, fg]   = avatarColor(employee.id);
  const totalHours = schedule?.totalHours || 0;
  const isOvertime = totalHours >= 40;
  const role       = employee.role;
  const originAddr = editedAddress.trim();
  const hasAddress = !!originAddr;

  const distVals  = Object.values(distances || {});
  const anyValid  = distVals.some(d => d && d.miles != null);
  // Address present but no distances came back — almost always an incomplete address.
  const distFailed = hasAddress && !loadingDist && distVals.length > 0 && !anyValid;

  const applyAddress = () => {
    setEditingAddress(false);
    if (originAddr && onComputeDistances) onComputeDistances(originAddr);
  };

  const clientById = {};
  clients.forEach(c => { clientById[c.id] = c; });

  // Open shifts near the employee: role-matched, DNR clients excluded, ranked by proximity.
  const shiftCards = shifts
    .map(s => {
      const client = clientById[s.clientId] || clients.find(c => c.fullName === s.clientName) || null;
      const dist   = client ? distances[client.id] : null;
      return {
        ...s, client, dist,
        preferred: client ? isPreferred(employee, client) : false,
        dnr:       client ? isDNR(employee, client) : false,
        hours:     shiftHours(s.scheduledStart, s.scheduledEnd),
      };
    })
    .filter(s => (!role || s.role === role) && !s.dnr)
    .sort((a, b) => {
      if (a.preferred !== b.preferred) return a.preferred ? -1 : 1;
      return (parseFloat(a.dist?.miles) || 9999) - (parseFloat(b.dist?.miles) || 9999);
    });

  // Client locations near the employee, ranked by proximity (DNR pulled to a separate section).
  const locCards = clients
    .map(c => ({ ...c, dist: distances[c.id], preferred: isPreferred(employee, c), dnr: isDNR(employee, c) }))
    .sort((a, b) => {
      if (a.preferred !== b.preferred) return a.preferred ? -1 : 1;
      return (parseFloat(a.dist?.miles) || 9999) - (parseFloat(b.dist?.miles) || 9999);
    });
  const okLocs  = locCards.filter(c => !c.dnr);
  const dnrLocs = locCards.filter(c => c.dnr);

  return (
    <div className="employee-panel">
      {/* ── Employee header ── */}
      <div className="emp-header">
        <div className="emp-avatar" style={{ background: bg, color: fg }}>
          {initialsFor(employee.firstName, employee.lastName)}
        </div>
        <div className="emp-id">
          <div className="emp-name">
            {employee.firstName} {employee.lastName}
            {isOvertime ? (
              <span className="overtime-badge" title={`${totalHours.toFixed(1)} hrs this week — at or over 40`}>
                ⚠ {totalHours.toFixed(1)}h this week
              </span>
            ) : (totalHours > 0 && (
              <span className="hours-badge">{totalHours.toFixed(1)}h this week</span>
            ))}
          </div>
          <div className="emp-meta">
            {role && <span className={`role-chip role-${role.toLowerCase()}`}>{role}</span>}
            {employee.phone && <span className="emp-phone">{employee.phone}</span>}
          </div>
          {employee.shiftPref?.length > 0 && (
            <div className="pref-chips" style={{ marginTop: 6 }} title="Shift preference (in the caregiver's ranked order)">
              {employee.shiftPref.map(p => <span key={p} className={`pref-chip ${prefClass(p)}`}>{shortPref(p)}</span>)}
            </div>
          )}
          {editingAddress ? (
            <div className="emp-addr-edit">
              <input
                className="emp-addr-input"
                value={editedAddress}
                onChange={e => setEditedAddress(e.target.value)}
                placeholder="Street, city, state, ZIP…"
                onKeyDown={e => { if (e.key === "Enter") applyAddress(); }}
                autoFocus
              />
              <button className="emp-link-btn" onClick={applyAddress}>✓ Use this address</button>
            </div>
          ) : (
            <div className="emp-addr-row">
              <span className={`emp-address ${hasAddress ? "" : "warn"}`}>
                {hasAddress ? originAddr : "⚠ No address on file"}
              </span>
              <button className="emp-link-btn" onClick={() => setEditingAddress(true)}>✎ Edit</button>
            </div>
          )}
        </div>
        <div className="emp-actions">
          {callUrl(employee.phone) && (
            <a className="action-btn primary" href={callUrl(employee.phone)} title={`Call ${employee.phone}`}>
              📞 Call
            </a>
          )}
          <button className="action-btn secondary" onClick={() => setShowSchedule(true)} title="View weekly schedule">
            📅 Schedule
          </button>
        </div>
      </div>

      {(distFailed || !hasAddress) && (
        <div className="emp-warn">
          ⚠ Couldn't calculate distances — {employee.firstName}'s address {hasAddress ? "couldn't be located" : "is missing"}.
          Click <strong>✎ Edit</strong> above to enter a full street, city, state, and ZIP, then “Use this address.”
        </div>
      )}

      {/* ── View toggle ── */}
      <div className="emp-toggle">
        <button className={view === "shifts" ? "active" : ""} onClick={() => setView("shifts")}>
          Open shifts <span className="emp-toggle-count">{shiftCards.length}</span>
        </button>
        <button className={view === "locations" ? "active" : ""} onClick={() => setView("locations")}>
          Client locations <span className="emp-toggle-count">{okLocs.length}</span>
        </button>
      </div>

      {loadingDist && (
        <div className="emp-loading"><span className="spinner" /> Calculating proximity…</div>
      )}

      {/* ── Open shifts ── */}
      {!loadingDist && view === "shifts" && (
        <div className="emp-list">
          {shiftCards.length === 0 && (
            <div className="emp-empty-row">No open {role} shifts available for {employee.firstName}.</div>
          )}
          {shiftCards.map((s, i) => (
            <div key={s.id} className={`emp-card ${s.preferred ? "preferred" : ""}`}>
              <div className="emp-rank">#{i + 1}</div>
              <div className="emp-card-main">
                <div className="emp-card-title">
                  {s.clientName || "Unknown Client"}
                  {s.preferred && <span className="preferred-tag">★ Preferred</span>}
                </div>
                <div className="emp-card-sub">
                  {s.role && <span className={`role-chip role-${s.role.toLowerCase()}`}>{s.role}</span>}
                  <span className="emp-when">{fmtDate(s.scheduledStart)} · {fmtTime(s.scheduledStart)}–{fmtTime(s.scheduledEnd)}</span>
                  {s.hours != null && <span className="emp-shift-hrs">{s.hours.toFixed(1)}h</span>}
                </div>
                <DistLine dist={s.dist} />
                {s.client?.address && <div className="emp-card-addr">{s.client.address}</div>}
              </div>
              <div className="emp-card-actions">
                {s.client?.address && hasAddress && (
                  <a className="action-btn secondary" href={mapsUrl(originAddr, s.client.address)} target="_blank" rel="noopener noreferrer">
                    ↗ Directions
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Client locations ── */}
      {!loadingDist && view === "locations" && (
        <div className="emp-list">
          {okLocs.map((c, i) => (
            <div key={c.id} className={`emp-card ${c.preferred ? "preferred" : ""}`}>
              <div className="emp-rank">#{i + 1}</div>
              <div className="emp-card-main">
                <div className="emp-card-title">
                  {c.fullName}
                  {c.preferred && <span className="preferred-tag">★ Preferred</span>}
                </div>
                <div className="emp-card-sub">
                  <span className={`client-type-chip type-${typeLabel(c.clientType).toLowerCase().replace(" ", "")}`}>{typeLabel(c.clientType)}</span>
                </div>
                <DistLine dist={c.dist} />
                {c.address && <div className="emp-card-addr">{c.address}</div>}
              </div>
              <div className="emp-card-actions">
                {c.address && hasAddress && (
                  <a className="action-btn secondary" href={mapsUrl(originAddr, c.address)} target="_blank" rel="noopener noreferrer">
                    ↗ Directions
                  </a>
                )}
              </div>
            </div>
          ))}
          {dnrLocs.length > 0 && (
            <>
              <div className="section-divider">DNR — Do Not Return — excluded for this employee</div>
              {dnrLocs.map(c => (
                <div key={c.id} className="emp-card dnr">
                  <div className="emp-rank">✕</div>
                  <div className="emp-card-main">
                    <div className="emp-card-title">{c.fullName}</div>
                    <div className="dnr-reason">Excluded — Do Not Return</div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {showSchedule && (
        <ScheduleModal
          worker={employee}
          schedule={schedule}
          weekStart={weekStart}
          weekEnd={weekEnd}
          onClose={() => setShowSchedule(false)}
        />
      )}
    </div>
  );
}
